import PropTypes from 'prop-types';
import { Box, LinearProgress, Typography } from '@mui/material';
import {
  formatDisplayPercent,
  formatPercentagePointsAboveAverage,
  formatStyleBarLabel,
  leadingStyleGroups,
  relativeStyleBarValue,
  styleBarsForIdentity,
} from '../../utils/nhStyleFingerprintSnapshot';

const REGULAR_BAR_HEIGHT = 4;
const DISTINCTIVE_BAR_HEIGHT = REGULAR_BAR_HEIGHT * 2;

const DISTINCTIVE_KEY_CAPTION = 'Distinctive styles have higher representation than the state average';
const LEADING_KEY_CAPTION = 'Leading styles are the ones with the largest shares';

function largestDisplayedTenths(bars) {
  return bars.reduce((largest, bar) => {
    const tenths = Number(bar.displayPercentTenths);
    if (!Number.isFinite(tenths)) {
      return largest;
    }
    return Math.max(largest, tenths);
  }, 0);
}

function StyleBarKey({ sample, caption }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {sample}
      <Typography variant="body2">{caption}</Typography>
    </Box>
  );
}

StyleBarKey.propTypes = {
  sample: PropTypes.node.isRequired,
  caption: PropTypes.string.isRequired,
};

function DistinctiveStyleKey() {
  return (
    <StyleBarKey
      caption={DISTINCTIVE_KEY_CAPTION}
      sample={(
        <LinearProgress
          variant="determinate"
          color="secondary"
          value={100}
          role="presentation"
          aria-hidden
          sx={{ width: 48, height: DISTINCTIVE_BAR_HEIGHT, flexShrink: 0 }}
        />
      )}
    />
  );
}

function LeadingStyleKey() {
  return (
    <StyleBarKey
      caption={LEADING_KEY_CAPTION}
      sample={(
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          Style
        </Typography>
      )}
    />
  );
}

function StyleBarKeys() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
      <DistinctiveStyleKey />
      <LeadingStyleKey />
    </Box>
  );
}

function StyleBarLabel({ bar, leading }) {
  const weight = leading ? 700 : 400;
  const summary = `${bar.group}, ${bar.count}, ${formatDisplayPercent(bar.displayPercentTenths)}`;
  return (
    <Typography variant="body2" component="p">
      <Box component="span" sx={{ fontWeight: weight }}>
        {summary}
      </Box>
      {Number.isFinite(bar.gapTenths)
        ? `, ${formatPercentagePointsAboveAverage(bar.gapTenths)}`
        : null}
    </Typography>
  );
}

const styleBarShape = PropTypes.shape({
  group: PropTypes.string.isRequired,
  count: PropTypes.number,
  displayPercentTenths: PropTypes.number,
  gapTenths: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
});

StyleBarLabel.propTypes = {
  bar: styleBarShape.isRequired,
  leading: PropTypes.bool.isRequired,
};

function NhStyleFingerprintStyleBar({ bar, maxTenths, leading }) {
  const label = formatStyleBarLabel(bar);
  const distinctive = Number.isFinite(bar.gapTenths);
  const barHeight = distinctive ? DISTINCTIVE_BAR_HEIGHT : REGULAR_BAR_HEIGHT;
  return (
    <Box sx={{ mb: 1.5 }}>
      <StyleBarLabel bar={bar} leading={leading} />
      <LinearProgress
        variant="determinate"
        color={distinctive ? 'secondary' : 'primary'}
        value={relativeStyleBarValue(bar.displayPercentTenths, maxTenths)}
        aria-label={label}
        sx={{ height: barHeight }}
      />
    </Box>
  );
}

NhStyleFingerprintStyleBar.propTypes = {
  bar: styleBarShape.isRequired,
  maxTenths: PropTypes.number.isRequired,
  leading: PropTypes.bool.isRequired,
};

function NhStyleFingerprintStyleBars({ identity }) {
  const bars = styleBarsForIdentity(identity);
  const maxTenths = largestDisplayedTenths(bars);
  const leadingGroups = new Set(leadingStyleGroups(bars));
  return (
    <Box sx={{ my: 2 }}>
      {bars.length > 0 ? <StyleBarKeys /> : null}
      {bars.map((bar) => (
        <NhStyleFingerprintStyleBar
          key={bar.group}
          bar={bar}
          maxTenths={maxTenths}
          leading={leadingGroups.has(bar.group)}
        />
      ))}
    </Box>
  );
}

NhStyleFingerprintStyleBars.propTypes = {
  identity: PropTypes.shape({
    styleGroups: PropTypes.arrayOf(PropTypes.shape({})),
    distinctiveFocus: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

export default NhStyleFingerprintStyleBars;
